import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Save, Image as ImageIcon } from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { sanitizeForFirestore } from '../../utils/firestoreUtils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import RoleBadge from '../../components/RoleBadge';
import { toast } from 'sonner';
import { isAdminLike } from '../../utils/roles';

const canEditCompany = (role) => isAdminLike(role);

const CompanyProfile = () => {
  const { profile, isAuthLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [initialCompanyName, setInitialCompanyName] = useState('');
  const [contact, setContact] = useState({
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
  });

  const [branding, setBranding] = useState({ logoUrl: '', brandColorPrimary: '' });
  const [googleReviewLink, setGoogleReviewLink] = useState('');


  const companyId = profile?.companyId || null;
  const contactSaveTimer = useRef(null);
  // Roles are company-scoped; use RoleContext for accurate RBAC
  // TODO: If we need more granular permissions for settings, extend RoleContext (without changing roles).
  const { role } = useRole();
  const canEdit = useMemo(() => canEditCompany(role), [role]);

  useEffect(() => {
    const loadCompany = async () => {
      if (isAuthLoading) return;
      if (!db) return;
      if (!companyId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const companyRef = doc(db, 'companies', companyId);
        const snap = await getDoc(companyRef);
        const data = snap.exists() ? snap.data() || {} : {};
        const name = data.name || '';
        const brandingData = data.branding || {};
        const contactData = data.contact || {};

        setCompanyName(name);
        setInitialCompanyName(name);
        setBranding({
          logoUrl: brandingData.logoUrl || '',
          brandColorPrimary: brandingData.brandColorPrimary || '',
        });
        setGoogleReviewLink(data.googleReviewLink || '');
        setContact({
          phone: contactData.phone || '',
          addressLine1: contactData.addressLine1 || '',
          addressLine2: contactData.addressLine2 || '',
          city: contactData.city || '',
          state: contactData.state || '',
          postalCode: contactData.postalCode || '',
        });
      } catch (e) {
        console.error('Failed to load company:', e);
        toast.error('Failed to load company profile');
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [companyId, isAuthLoading]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId) return;

    const nextName = companyName.trim();
    if (!nextName) {
      toast.error('Company name is required');
      return;
    }

    if (nextName === initialCompanyName.trim()) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const companyRef = doc(db, 'companies', companyId);
      await setDoc(
        companyRef,
        sanitizeForFirestore({
          name: nextName,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      setInitialCompanyName(nextName);
      toast.success('Company name updated');
    } catch (e) {
      console.error('Failed to update company:', e);
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !companyId || !storage) return;

    try {
      const fileRef = ref(storage, `companies/${companyId}/branding/logo`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const companyRef = doc(db, 'companies', companyId);
      await setDoc(
        companyRef,
        sanitizeForFirestore({
          branding: {
            ...(branding || {}),
            logoUrl: url,
          },
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );

      setBranding((prev) => ({ ...prev, logoUrl: url }));
      toast.success('Logo updated');
    } catch (e) {
      console.error('Failed to upload logo:', e);
      toast.error('Failed to upload logo');
    } finally {
      // reset file input
      event.target.value = '';
    }
  };

  const handleBrandColorChange = async (value) => {
    if (!companyId) return;
    const next = value.trim();
    setBranding((prev) => ({ ...prev, brandColorPrimary: next }));

    try {
      const companyRef = doc(db, 'companies', companyId);
      await setDoc(
        companyRef,
        sanitizeForFirestore({
          branding: {
            ...(branding || {}),
            brandColorPrimary: next || null,
          },
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      toast.success('Brand color updated');
    } catch (e) {
      console.error('Failed to update brand color:', e);
      toast.error('Failed to update brand color');
    }
  };

  const handleContactFieldChange = (field, value) => {
    setContact((prev) => ({ ...prev, [field]: value }));
  };

  const handleGoogleReviewLinkBlur = async () => {
    if (!companyId) return;
    try {
      const companyRef = doc(db, 'companies', companyId);
      await setDoc(
        companyRef,
        sanitizeForFirestore({ googleReviewLink: googleReviewLink.trim() || null, updatedAt: serverTimestamp() }),
        { merge: true }
      );
      toast.success('Google Review link saved');
    } catch (e) {
      console.error('Failed to save review link:', e);
      toast.error('Failed to save review link');
    }
  };

  const handleContactBlur = () => {
    if (!companyId) return;
    if (contactSaveTimer.current) clearTimeout(contactSaveTimer.current);
    contactSaveTimer.current = setTimeout(async () => {
      try {
        const companyRef = doc(db, 'companies', companyId);
        await setDoc(
          companyRef,
          sanitizeForFirestore({
            contact: {
              phone: contact.phone || null,
              addressLine1: contact.addressLine1 || null,
              addressLine2: contact.addressLine2 || null,
              city: contact.city || null,
              state: contact.state || null,
              postalCode: contact.postalCode || null,
            },
            updatedAt: serverTimestamp(),
          }),
          { merge: true }
        );
        toast.success('Contact information updated');
      } catch (e) {
        console.error('Failed to update contact information:', e);
        toast.error('Failed to update contact information');
      }
    }, 750);
  };

  if (isAuthLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-secondary">Company Profile</h2>
            <p className="text-sm text-muted-foreground">Your account is missing a companyId.</p>
          </div>
        </div>
        <Card className="p-6">
          <p className="text-sm text-gray-600">
            If you just signed in, refresh once. If this persists, your membership bootstrap may be blocked by Firestore rules.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-secondary">Company Profile</h2>
            <p className="text-sm text-muted-foreground">Update your company name and branding.</p>
          </div>
        </div>
        {role && <RoleBadge role={role} />}
      </div>

      <Card className="p-6 space-y-6">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="My Company"
              disabled={!canEdit || saving}
            />
          </div>

          <div className="flex items-center justify-end">
            <Button type="submit" disabled={!canEdit || saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>

          {!canEdit && (
            <p className="text-xs text-muted-foreground">
              Only owners and admins can edit company details.
            </p>
          )}
        </form>

        {/* Contact section */}
        <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
          <h3 className="text-sm font-semibold text-secondary">Contact Information</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyPhone">Phone</Label>
              <Input
                id="companyPhone"
                type="tel"
                value={contact.phone}
                onChange={(e) => handleContactFieldChange('phone', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="(555) 123-4567"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="addressLine1">Address line 1</Label>
              <Input
                id="addressLine1"
                value={contact.addressLine1}
                onChange={(e) => handleContactFieldChange('addressLine1', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="123 Main St"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="addressLine2">Address line 2 (optional)</Label>
              <Input
                id="addressLine2"
                value={contact.addressLine2}
                onChange={(e) => handleContactFieldChange('addressLine2', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="Suite 200"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={contact.city}
                onChange={(e) => handleContactFieldChange('city', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="Anytown"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State / Region</Label>
              <Input
                id="state"
                value={contact.state}
                onChange={(e) => handleContactFieldChange('state', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="CA"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal code</Label>
              <Input
                id="postalCode"
                value={contact.postalCode}
                onChange={(e) => handleContactFieldChange('postalCode', e.target.value)}
                onBlur={handleContactBlur}
                placeholder="94110"
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        {/* Branding section */}
        <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
          <h3 className="text-sm font-semibold text-secondary">Branding</h3>

          {/* Logo upload */}
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg border border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden">
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt="Company logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-gray-400 flex flex-col items-center justify-center">
                    <ImageIcon className="h-4 w-4 mb-1" />
                    No logo
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={!canEdit}
                  className="text-xs"
                />
                <p className="text-[11px] text-muted-foreground">
                  PNG or JPG. Square logos work best.
                </p>
              </div>
            </div>
          </div>

          {/* Primary brand color */}
          <div className="space-y-2">
            <Label htmlFor="brandColor">Primary brand color (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="brandColor"
                type="text"
                value={branding.brandColorPrimary || ''}
                onChange={(e) => handleBrandColorChange(e.target.value)}
                placeholder="#0A2647"
                disabled={!canEdit}
                className="text-sm"
              />
              <div
                className="w-6 h-6 rounded border border-gray-300"
                style={{
                  backgroundColor: branding.brandColorPrimary || '#0A2647',
                }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Used for quote accents and headings.
            </p>
          </div>
        </div>
      </Card>

      {isAdminLike(role) && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-secondary mb-3">Review & Reputation</h3>
          <div className="space-y-2">
            <Label htmlFor="googleReviewLink">Google Review Link</Label>
            <Input
              id="googleReviewLink"
              type="url"
              value={googleReviewLink}
              onChange={(e) => setGoogleReviewLink(e.target.value)}
              onBlur={handleGoogleReviewLinkBlur}
              placeholder="https://g.page/r/your-business/review"
              disabled={!canEdit}
              data-testid="google-review-link-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Paste your Google Business review link. Used when sending review requests to completed jobs.
            </p>
          </div>
        </Card>
      )}

      {isAdminLike(role) && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-secondary mb-3">Debug (Admin only)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">companyId</p>
              <p className="font-mono text-xs break-all">{companyId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">role</p>
              <p className="font-mono text-xs">{role}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-muted-foreground">company name</p>
              <p className="font-mono text-xs break-words">{companyName || '(empty)'} </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Read-only; visible to owner/admin only.
          </p>
        </Card>
      )}
    </div>
  );
};

export default CompanyProfile;
